import Alpine from 'alpinejs';
import { App } from './app';
import { Cognito } from './cognito';
import { Api } from './api';
import { Utils } from './utils';
import { formatDate } from './config';

const cognito = new Cognito();
const api = new Api();
const app = new App(api);

// Session renewal: silently refresh the id token when it nears expiry / on a 401,
// and bounce to the Hosted UI login when the refresh token itself has expired.
api.setAuthHandlers({
    refresh: () => cognito.refresh(),
    onExpired: () => { window.location.href = cognito.url('login'); },
});

(window as any).Alpine = Alpine;

// Info/error/table dialog store — rendered (escaped) by the modal in index.html.
Alpine.store('dialog', {
    open: false,
    kind: 'info' as 'info' | 'error' | 'table',
    title: '',
    rows: [] as { label: string; value: string }[],
    headers: [] as { key: string; label: string }[],
    tableRows: [] as Record<string, any>[],
    show(title: string, rows: { label: string; value: string }[]) {
        this.kind = 'info'; this.title = title; this.rows = rows; this.open = true;
    },
    showError(title: string, rows: { label: string; value: string }[]) {
        this.kind = 'error'; this.title = title; this.rows = rows; this.open = true;
    },
    showTable(title: string, headers: { key: string; label: string }[], tableRows: Record<string, any>[]) {
        this.kind = 'table'; this.title = title; this.headers = headers; this.tableRows = tableRows; this.open = true;
    },
    close() { this.open = false; }
});

// UI view/visibility state. App pushes to this; the markup binds x-show to it.
Alpine.store('ui', {
    loggedIn: false,
    view: 'grid' as 'grid' | 'client' | 'log',
    activeForm: null as string | null,
});

// Client life-event / transaction rows, rendered by x-for tables in client.html.
Alpine.store('tables', {
    events: [] as Record<string, any>[],
    transactions: [] as Record<string, any>[],
});

// Light/dark theme toggle (navbar). corporate = light, forest = dark. Persisted to
// localStorage and applied to <html data-theme>. An inline script in index.html
// sets the initial theme before first paint to avoid a flash; init() re-syncs.
Alpine.store('theme', {
    dark: (() => { try { return localStorage.getItem('theme') === 'forest'; } catch (e) { return false; } })(),
    init() { this.apply(); },
    apply() {
        const t = this.dark ? 'forest' : 'corporate';
        document.documentElement.setAttribute('data-theme', t);
        try { localStorage.setItem('theme', t); } catch (e) { /* ignore */ }
    },
    toggle() { this.dark = !this.dark; this.apply(); },
});

// Bridge so markup can call App methods via @click (e.g. @click="event('listview')").
Alpine.data('lisa', () => ({
    event: (action: string, payload?: any) => app.event(action, payload),
    send: (action: string) => app.send(action),
    get: (action: string, id: string = '') => app.get(action, id),
    setPageSize: (size: number | string) => app.setPageSize(size),
    info: (action: string, id: string = '') => app.info(action, id),
    fmtDate: (value: any) => formatDate(value),
    login: (which: string) => { window.location.href = cognito.url(which); },
    logout: async () => {
        await cognito.logout();
        api.setToken('');
        window.location.href = 'index.html';
    },
    authenticate: () => { window.location.href = app.hmrcUrl(); },
    closeForm: (el: HTMLElement) => {
        el.closest('.modal-box')?.classList.add('hidden');
        (window as any).Alpine.store('ui').activeForm = null;
    },
}));

Alpine.start();

// Handle the OAuth/login redirect back to the app
window.addEventListener('load', () => {
    const handleLoad = async () => {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');  // from Cognito OR HMRC
        const state = urlParams.get('state');  // HMRC
        const test = urlParams.get('test'); // useful for testing

        let token = "";

        if (code) {
            if (state) {
                // HMRC OAuth callback — verify the nonce to prevent CSRF
                const storedNonce = localStorage.getItem('hmrc_state');
                if (!storedNonce || storedNonce !== state) {
                    Utils.showInfo("Error", { message: "Invalid state parameter — possible CSRF attempt" });
                    return;
                }
                localStorage.removeItem('hmrc_state');

                token = localStorage.getItem('jwt') ?? '';
                if (!token) {
                    Utils.showInfo("Error", { message: "Session expired — please log in again" });
                    return;
                }
                api.setToken(token);
                await api.authorize(code);
            }
            else {
                token = await cognito.convertToToken(code);
                api.setToken(token);
            }

            (window as any).Alpine.store('ui').loggedIn = true;
            await app.initialize();
        }
        else if (test) {
            api.setToken(test);
            (window as any).Alpine.store('ui').loggedIn = true;
            await app.initialize();
        }
    };

    handleLoad();
});
